import { useEffect, useRef, useState } from "react";
import api, { errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Upload, KeyRound, Trash2, Copy } from "lucide-react";

async function fetchSignatureUrl(uid) {
  try {
    const res = await api.get(`/users/${uid}/signature`, { responseType: "blob" });
    return URL.createObjectURL(res.data);
  } catch { return null; }
}

export function MySignatureSection({ users = [] }) {
  const { t } = useI18n();
  const { user, can } = useAuth();
  const [targetId, setTargetId] = useState(user?.id);
  const [imgUrl, setImgUrl] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const isSelf = targetId === user?.id;
  const target = users.find((u) => u.id === targetId) || user;

  useEffect(() => { if (targetId) fetchSignatureUrl(targetId).then(setImgUrl); }, [targetId]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(isSelf ? "/auth/me/signature" : `/users/${targetId}/signature`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("common.saved"));
      setImgUrl(await fetchSignatureUrl(targetId));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async () => {
    try { await api.delete(`/users/${targetId}/signature`); setImgUrl(null); toast.success(t("common.saved")); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const generate = async () => {
    if (!window.confirm(t("set.tokenHint"))) return;
    try { const { data } = await api.post("/auth/me/signature-token"); setToken(data.token); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="border bg-card p-5 mb-4" data-testid="signature-section">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">{t("set.mySignature")}</div>
      <label className="flex items-center gap-2 text-xs mb-3 cursor-pointer" data-testid="email-notifications-toggle">
        <input type="checkbox" checked={user?.email_notifications !== false}
          onChange={async (e) => { try { await api.put("/auth/me/preferences", { email_notifications: e.target.checked }); toast.success(t("common.saved")); window.location.reload(); } catch (err) { toast.error(errMsg(err)); } }} />
        <span className="font-bold">{t("set.emailNotifications")}</span> <span className="text-muted-foreground">({user?.email})</span>
      </label>
      <label className="flex items-center gap-2 text-xs mb-3 cursor-pointer" data-testid="daily-digest-toggle">
        <input type="checkbox" checked={user?.daily_digest !== false}
          onChange={async (e) => { try { await api.put("/auth/me/preferences", { daily_digest: e.target.checked }); toast.success(t("common.saved")); window.location.reload(); } catch (err) { toast.error(errMsg(err)); } }} />
        <span className="font-bold">{t("set.dailyDigestPref")}</span>
      </label>
      {can("admin") && users.length > 0 && (
        <div className="mb-3 max-w-sm space-y-1">
          <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.signatureFor")}</Label>
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger className="rounded-none h-8 text-xs" data-testid="signature-user-select"><SelectValue /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email} — {u.email}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div className="flex gap-6 flex-wrap">
        <div className="space-y-2">
          <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.signatureImage")}</Label>
          <div className="border bg-white w-64 h-24 flex items-center justify-center" data-testid="signature-preview">
            {imgUrl ? <img src={imgUrl} alt="firma" className="max-h-20 max-w-60 object-contain" /> : <span className="text-[10px] text-muted-foreground">—</span>}
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="signature-file-input" />
            <Button size="sm" variant="outline" className="rounded-none text-xs h-7" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="signature-upload-btn">
              <Upload size={12} className="mr-1" /> {busy ? "…" : t("it.imageUpload")}
            </Button>
            {imgUrl && (
              <Button size="sm" variant="ghost" className="rounded-none text-xs h-7 text-destructive" onClick={remove} data-testid="signature-remove-btn">
                <Trash2 size={12} className="mr-1" /> {t("it.imageRemove")}
              </Button>
            )}
          </div>
        </div>
        {isSelf && (
          <div className="space-y-2">
            <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.signatureToken")}</Label>
            <div className="text-xs" data-testid="signature-token-status">
              {target?.signature_token_created_at ? <span className="text-emerald-700 font-bold">{t("set.tokenConfigured")}</span> : <span className="text-muted-foreground">{t("set.tokenMissing")}</span>}
            </div>
            {token && (
              <div className="flex items-center gap-2 border bg-muted px-3 py-2" data-testid="signature-token-value">
                <span className="font-mono text-base font-black tracking-widest">{token}</span>
                <button onClick={() => { navigator.clipboard?.writeText(token); toast.success(t("set.copied")); }} className="text-primary"><Copy size={13} /></button>
              </div>
            )}
            <Button size="sm" variant="outline" className="rounded-none text-xs h-7" onClick={generate} data-testid="signature-generate-token-btn">
              <KeyRound size={12} className="mr-1" /> {t("set.generateToken")}
            </Button>
            <p className="text-[9px] text-muted-foreground max-w-xs">{t("set.tokenHint")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
